import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';

import { useWithdrawCertificatePermission } from '../../../../../../../../hooks/api/certificates';
import useDialogContext from '../../../../../../../../hooks/dialog/useDialogContext';
import useNotificationContext from '../../../../../../../../hooks/notification/useNotificationContext';
import { ToastSeverity } from '../../../../../../../../shared/enums/toast-severity';
import { getWithdrawCertificatePermissionErrorMessage } from '../../../../../../../../shared/helpers/get-withdraw-certificate-permission-error-message';

type WithdrawCertificateButtonProps = {
  permissionId: number;
  certificateId?: string;
};

const WithdrawCertificateButton = ({
  permissionId,
  certificateId,
}: WithdrawCertificateButtonProps): JSX.Element => {
  const { t } = useTranslation();
  const { openDialog } = useDialogContext();
  const { notify } = useNotificationContext();
  const queryClient = useQueryClient();

  const onSuccess = () => {
    if (!certificateId) {
      return;
    }

    queryClient.invalidateQueries({
      queryKey: ['certificate', certificateId, 'permissions'],
    });
  };

  const onError = (error: Error) => {
    notify({
      severity: ToastSeverity.ERROR,
      detail: getWithdrawCertificatePermissionErrorMessage(error, t),
      life: 3000,
    });
  };

  const { mutate: withdrawCertificatePermission } = useWithdrawCertificatePermission({
    onSuccess,
    onError,
  });

  const onConfirmWithdraw = () => {
    withdrawCertificatePermission({ permissionId });
  };

  return (
    <Button
      className='w-fit py-2 px-3 m-0'
      label={t('certificateDetailsPage.tabs.permissionsTab.buttons.withdraw')}
      onClick={() => {
        return openDialog('confirmation-dialog', {
          title: t('withdrawCertificateConfirmationDialog.title'),
          message: t('withdrawCertificateConfirmationDialog.content'),
          confirmButtonText: t('withdrawCertificateConfirmationDialog.buttons.withdraw'),
          cancelButtonText: t('withdrawCertificateConfirmationDialog.buttons.cancel'),
          onConfirm: onConfirmWithdraw,
        });
      }}
    />
  );
};

export default WithdrawCertificateButton;
